import React, { useContext, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Gallery } from '../Gallery';
import {GalleryAPIResponse} from '@moment/net/softbox-types.ts';
import {useLocation, useParams, useSearchParams} from 'react-router';

import './GalleryLayout.scss';
import { GalleryItemFactory } from '@moment/components/Gallery/types/GalleryItem.ts';
import ActivityIndicator from '@moment/components/ActivityIndicator/ActivityIndicator.tsx';
import {CommonPageDataContext} from "@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts";
import { useGalleryAnalytics } from '@moment/components/Gallery/hooks/useGalleryAnalytics';
import { useAdsConfigOverride } from '@moment/context/AdsConfigOverrideContext.tsx';
import { getWithOutSpecialChars } from '@pages/Root/utils';
import { useFotoscapeNavigate } from '@hooks/navigation';
import { useAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import { StoryDismissalService } from '@moment/analytics/StoryDismissalService.ts';

export interface GalleryLayoutProps {
  onPageChange?: () => void,
  queryFn: (options:any) => Promise<GalleryAPIResponse>
}

export const GalleryLayout: React.FC<GalleryLayoutProps> = ({onPageChange, queryFn}) => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const qFn = useMemo(() => queryFn(params), [params]);
  const context = useContext(CommonPageDataContext);
  const lang = i18n.language;
  const navigate = useFotoscapeNavigate();
  const analyticsService = useAnalytics();
  const storyDismissalService = useRef(
    new StoryDismissalService(analyticsService)
  ).current;

  const { data, isLoading, isError, error } = useQuery<
    GalleryAPIResponse,
    Error,
    GalleryAPIResponse
  >({
    queryKey: ['lookbook', params.lookbookId, lang], //constructed using a prefetchQueryKeyMutator
    queryFn: qFn,
    enabled: !!params.lookbookId,
  });

  const lookbook = useMemo(() => data?.lookbookResponse?.lookbook, [data]);
  const schedule = useMemo(() => searchParams.get('schedule') || undefined, [searchParams]);
  const { trackGallerySwipe, trackGalleryTap } = useGalleryAnalytics(lookbook, schedule);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        storyDismissalService.stopTracking('background');
      } else if (document.visibilityState === 'visible' && lookbook?.uid) {
        // Restart tracking when user returns to the tab
        storyDismissalService.startTracking(
          context.partner || '',
          searchParams,
          lookbook.uid
        );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle story change
    if (lookbook?.uid && storyDismissalService.isCurrentlyTracking() && storyDismissalService.currentlyTrackedContentId() !== lookbook.uid) {
      storyDismissalService.stopTracking('story_changed'); // Stop tracking old story
      storyDismissalService.startTracking(context.partner || '', searchParams, lookbook.uid); // Start tracking new one
    } else if (lookbook?.uid) {
      // Initial tracking start
      storyDismissalService.startTracking(context.partner || '', searchParams, lookbook.uid);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      storyDismissalService.stopTracking('unmount');
    };
  }, [lookbook?.uid, context.partner, searchParams, storyDismissalService]);

  // This cleanup effect is to ensure we stop tracking if the component unmounts
  // for any reason, even if the main effect doesn't run its cleanup.
  useEffect(() => () => storyDismissalService.stopTracking('unmount'), [storyDismissalService]);

  const handleGalleryPageChange = useCallback((pageIndex: number) => {
    trackGallerySwipe(pageIndex);
    onPageChange?.();
  }, [onPageChange, trackGallerySwipe]);

  useEffect(() => {
    if (!params.title && lookbook)
    {
      const currentPath = window.location.pathname;
      const lookbookId = params.lookbookId;
      const title = getWithOutSpecialChars(lookbook?.title[lang] || '');

      // Check if pathname already ends with a slash after lookbookId
      const expectedLookbookPath = `/lookbook/${lookbookId}`;
      const needsSlash = !currentPath.endsWith('/') && currentPath === expectedLookbookPath;

      const newPath = needsSlash
        ? `${currentPath}/${title}`
        : `${currentPath}${title}`;

      navigate({pathname: newPath}, {replace: true, state: location.state});
    }
  }, []);

  const errorContent = useMemo(() => {
    if (isError) {
      return <div className="gallery-status-overlay gallery-error">{t('galleryError')}: {error?.message || t('galleryCouldNotLoad')}</div>;
    }
    if (!data) {
      return <div className="gallery-status-overlay gallery-empty-data">{t('gallerySomethingWentWrong')}</div>;
    }
    const {lookbookResponse} = data;
    if (!lookbookResponse || !lookbookResponse.lookbook || lookbookResponse.lookbook.looks.length === 0) {
      return <div className="gallery-status-overlay gallery-empty-data">{t('gallerySomethingWentWrong')}</div>;
    }
    return null;
  }, [isError, error, data, t]);

  const utmSource = useMemo(() => searchParams.get('utm_source') || context.partner, [context.partner, searchParams]);
  const brandGroupId = useMemo(() => lookbook?.ownedBy?.brandGroup || context.brandGroupId, [context.brandGroupId, lookbook]);
  const showAd:boolean = useMemo(() => !!lookbook, [lookbook]);
  // The single root AdsLayout waits for this on lookbook routes. Publish as soon as the lookbook is
  // known; once loading has settled without one (error/empty) fall back to the default config so
  // the root is not left waiting.
  const adsOverride = useMemo(
    () => (lookbook || !isLoading ? { brandGroupId, utmSource: utmSource || '' } : null),
    [lookbook, isLoading, brandGroupId, utmSource]
  );
  useAdsConfigOverride(adsOverride);
  const gItems = useMemo(() => lookbook ? new GalleryItemFactory(lookbook).createAllGalleryItems() : [], [lookbook])

  if (isLoading) {
    return <div className="gallery-status-overlay gallery-loading">
      <ActivityIndicator />
    </div>;
  }

  if (errorContent) {
    return errorContent;
  }

  const gallery = showAd && (
      <Gallery
          key={lookbook?.uid}
          items={gItems}
          lookbookId={lookbook?.uid}
          onPageChange={handleGalleryPageChange}
          onPlayPauseClick={trackGalleryTap}
          lookbookOwnedBy={lookbook?.ownedBy}
          interests={lookbook?.interests?.map(item => item.uid) ?? []}
      />
  );

  return (
    <div className="gallery-container">
      {gallery}
    </div>
  );
};
